import { Box, Button, Flex, Spinner, Alert } from '@chakra-ui/react';
import type { EVMGasPaymentMethod } from '@halo/wallet-app';
import { type TronTransactionFeeResult } from '@halo/wallet-app';
import {
  ChainType,
  type CoinState,
  type BaseTransactionFeeResult,
  type EVMTransactionFeeResult,
} from '@halo/wallet-app';
import { useTranslation } from 'react-i18next';
import { useWalletStore } from '@/wallet';
import { PaymentMethodSelector } from './payment-method-selector';
import { GasSettingsPanel, type EvmGasSettingInfo } from './gas-settings-panel';
import DownIcon from './images/down.svg?react';
import type { UseGasFeeResult } from './useGasFee';

export interface GasFeeLayoutProps {
  children: React.ReactNode;
}

export function GasFeeLayout({ children }: GasFeeLayoutProps) {
  return (
    <Flex pt="3.5" alignItems="center" justifyContent="space-between">
      <Flex gap="1" alignItems="center">
        {/* TODO amo: Optimize this icon */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
        >
          <path
            d="M2.8125 15.75C2.66332 15.75 2.52024 15.6907 2.41475 15.5852C2.30926 15.4798 2.25 15.3367 2.25 15.1875C2.25 15.0383 2.30926 14.8952 2.41475 14.7898C2.52024 14.6843 2.66332 14.625 2.8125 14.625H3.375V4.5C3.375 3.25688 4.52587 2.25 5.94675 2.25H9.80325C11.2241 2.25 12.375 3.25688 12.375 4.5V14.625H12.9375C13.0867 14.625 13.2298 14.6843 13.3352 14.7898C13.4407 14.8952 13.5 15.0383 13.5 15.1875C13.5 15.3367 13.4407 15.4798 13.3352 15.5852C13.2298 15.6907 13.0867 15.75 12.9375 15.75H2.8125ZM9.9 3.375H5.85C5.15812 3.375 4.58662 3.80925 4.509 4.36837L4.5 4.5V14.625H11.25V4.5C11.25 3.92287 10.7291 3.447 10.0575 3.38287L9.9 3.375Z"
            fill="#1A1A1A"
          />
          <path
            d="M14.0625 5.625C14.1942 5.62496 14.3217 5.67109 14.4228 5.75538C14.5239 5.83966 14.5923 5.95675 14.616 6.08625L14.625 6.1875V11.8125C14.625 12.232 14.4687 12.6364 14.1867 12.947C13.9046 13.2575 13.5171 13.4519 13.0995 13.4921L12.9375 13.5H11.8125C11.6719 13.5003 11.5364 13.4479 11.4325 13.3532C11.3286 13.2585 11.264 13.1283 11.2513 12.9883C11.2386 12.8483 11.2787 12.7087 11.3639 12.5968C11.449 12.485 11.5729 12.409 11.7112 12.384L11.8125 12.375H12.9375C13.0692 12.375 13.1967 12.3289 13.2978 12.2446C13.3989 12.1603 13.4673 12.0433 13.491 11.9138L13.5 11.8125V6.1875C13.5 6.03832 13.5593 5.89524 13.6648 5.78975C13.7702 5.68426 13.9133 5.625 14.0625 5.625Z"
            fill="#1A1A1A"
          />
          <path
            d="M13.1017 5.22682C13.1949 5.13381 13.3178 5.07645 13.449 5.06473C13.5801 5.05301 13.7113 5.08768 13.8195 5.1627L13.8982 5.22682L15.5857 6.91432C15.6854 7.01353 15.7443 7.14647 15.7508 7.28695C15.7573 7.42742 15.7109 7.56523 15.6209 7.67323C15.5308 7.78122 15.4035 7.85156 15.2642 7.87039C15.1248 7.88922 14.9835 7.85517 14.868 7.77494L14.7892 7.71082L13.1017 6.02332C13.0493 5.97107 13.0078 5.909 12.9794 5.84066C12.9511 5.77232 12.9365 5.69906 12.9365 5.62507C12.9365 5.55108 12.9511 5.47782 12.9794 5.40948C13.0078 5.34114 13.0493 5.27907 13.1017 5.22682ZM9.56248 5.62507C9.70305 5.62481 9.83861 5.67719 9.94249 5.77189C10.0464 5.86659 10.111 5.99675 10.1237 6.13674C10.1364 6.27673 10.0962 6.41641 10.0111 6.52826C9.92598 6.64011 9.80205 6.71603 9.66373 6.74107L9.56248 6.75007H6.18748C6.04692 6.75033 5.91135 6.69795 5.80748 6.60325C5.7036 6.50855 5.63895 6.37839 5.62625 6.2384C5.61355 6.09841 5.65372 5.95873 5.73886 5.84688C5.82399 5.73503 5.94792 5.65911 6.08623 5.63407L6.18748 5.62507H9.56248Z"
            fill="#1A1A1A"
          />
        </svg>
        <Box fontSize="xs" fontWeight="semibold" color="fg.muted">
          Gas fee
        </Box>
      </Flex>

      {children}
    </Flex>
  );
}

export interface CommonGasFeeProps {
  gasInfo: BaseTransactionFeeResult | undefined;
  mainCoin: CoinState | undefined;
  loading: boolean;
}

export function CommonGasFee({ gasInfo, mainCoin, loading }: CommonGasFeeProps) {
  const { coinAmountDisplay, currencyAmountDisplay } = useWalletStore();

  return (
    <GasFeeLayout>
      <Box fontSize="xs" fontWeight="medium" color="fg.muted">
        {loading ? (
          <Spinner size="xs" mr="1" />
        ) : (
          <>
            {coinAmountDisplay(gasInfo?.gasFee, mainCoin?.symbol)}
            {currencyAmountDisplay(gasInfo?.currencyGasFee, '≈')}
          </>
        )}
      </Box>
    </GasFeeLayout>
  );
}

export interface EVMGasFeeProps {
  showEvmPaymentMethod: boolean;
  paymentMethod: EVMGasPaymentMethod | undefined;
  onChangePaymentMethod: (evmPaymentMethod: EVMGasPaymentMethod) => void;
  gasLevelInfo: EvmGasSettingInfo | undefined;
  onChangeGasLevelInfo: (gasLevelInfo: EvmGasSettingInfo) => void;
  gasInfo: EVMTransactionFeeResult | undefined;
  loading: boolean;
  mainCoin: CoinState | undefined;
  container?: React.RefObject<HTMLDivElement | null>;
}

export function EVMGasFee({
  showEvmPaymentMethod,
  paymentMethod,
  onChangePaymentMethod,
  gasLevelInfo,
  onChangeGasLevelInfo,
  gasInfo,
  loading,
  mainCoin,
  container,
}: EVMGasFeeProps) {
  const { coinAmountDisplay, currencyAmountDisplay } = useWalletStore();

  return (
    <Box>
      <GasFeeLayout>
        {showEvmPaymentMethod && (
          <PaymentMethodSelector
            container={container}
            value={paymentMethod}
            onChange={onChangePaymentMethod}
          />
        )}
      </GasFeeLayout>
      <GasSettingsPanel
        container={container}
        paymentMethod={paymentMethod}
        gasInfo={gasInfo}
        mainCoin={mainCoin}
        gasLevelInfo={gasLevelInfo}
        onChangeGasLevelInfo={onChangeGasLevelInfo}
        triggerContent={
          loading || gasLevelInfo ? (
            <Flex
              mt="3"
              alignItems="center"
              gap="2.5"
              bg="white"
              px="3"
              py="3.5"
              borderRadius="xl"
              cursor="pointer"
              onClick={(e) => {
                if (loading) {
                  e.stopPropagation();
                  e.preventDefault();
                }
              }}
              onMouseDown={(e) => {
                if (loading) {
                  e.stopPropagation();
                  e.preventDefault();
                }
              }}
            >
              {loading ? (
                <Flex justifyContent="center" flex="1 1 auto">
                  <Spinner size="xs" />
                </Flex>
              ) : gasLevelInfo ? (
                <>
                  <Box marginRight="auto" minW="0" fontSize="xs" fontWeight="medium">
                    {coinAmountDisplay(gasLevelInfo.info.gasFee, mainCoin?.symbol)}
                    {currencyAmountDisplay(gasLevelInfo.info.currencyGasFee)}（
                    {gasLevelInfo.level}）
                  </Box>
                  <DownIcon />
                </>
              ) : null}
            </Flex>
          ) : null
        }
      />
    </Box>
  );
}

export interface TronGasFeeProps extends CommonGasFeeProps {
  gasInfo: TronTransactionFeeResult | undefined;
}

export function TronGasFee(props: TronGasFeeProps) {
  const { loading, gasInfo } = props;
  // TODO amo: validate tron isUsedEnergy
  if (gasInfo?.isUsedBandwidth) {
    return (
      <Flex pt="3.5" alignItems="center" justifyContent="space-between">
        <Flex gap="1" alignItems="center">
          <Box fontSize="xs" fontWeight="semibold" color="fg.muted">
            Bandwidth
          </Box>
        </Flex>

        <Box fontSize="xs" fontWeight="medium" color="fg.muted">
          {loading ? (
            <Spinner size="xs" mr="1" />
          ) : (
            <>
              {gasInfo.bandwidth}
              {' / '}
              {gasInfo.residualBandwidth}
            </>
          )}
        </Box>
      </Flex>
    );
  }

  return <CommonGasFee {...props} />;
}

export interface GasFeeProps extends UseGasFeeResult {
  container?: React.RefObject<HTMLDivElement | null>;
}

export function GasFee({
  loading,
  net,
  showEvmPaymentMethod,
  evmPaymentMethod,
  setEvmPaymentMethod,
  gasInfo,
  gasLevelInfo,
  setGasLevelInfo,
  mainCoin,
  error,
  getGasFeeInfo,
  container,
}: GasFeeProps) {
  const { t } = useTranslation();
  const main = useMemo(() => {
    switch (net?.chainType) {
      case ChainType.EVM:
        return (
          <EVMGasFee
            showEvmPaymentMethod={showEvmPaymentMethod}
            paymentMethod={evmPaymentMethod}
            onChangePaymentMethod={setEvmPaymentMethod}
            gasInfo={gasInfo as EVMTransactionFeeResult}
            loading={loading}
            gasLevelInfo={gasLevelInfo}
            onChangeGasLevelInfo={setGasLevelInfo}
            mainCoin={mainCoin}
            container={container}
          />
        );
      case ChainType.Tron:
        return (
          <TronGasFee
            gasInfo={gasInfo as TronTransactionFeeResult}
            mainCoin={mainCoin}
            loading={loading}
          />
        );
      default:
        return (
          <CommonGasFee
            gasInfo={gasInfo as BaseTransactionFeeResult}
            mainCoin={mainCoin}
            loading={loading}
          />
        );
    }
  }, [
    showEvmPaymentMethod,
    evmPaymentMethod,
    gasInfo,
    gasLevelInfo,
    loading,
    mainCoin,
    net?.chainType,
    setEvmPaymentMethod,
    setGasLevelInfo,
    container,
  ]);

  return (
    <Box>
      {main}
      {error && (
        <Alert.Root status="error" mt="3" color="white" size="sm" alignItems="center">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title wordBreak="break-all">{t('tips.failedToGetGasFee')}</Alert.Title>
          </Alert.Content>
          <Button size="xs" onClick={getGasFeeInfo}>
            {t('buttons.refresh')}
          </Button>
        </Alert.Root>
      )}
    </Box>
  );
}
